import { useMemo, useState } from "react";
import { Button, Checkbox, EmptyState, FormDialog, Skeleton, DialogCancel } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useShows } from "../../app/hooks/useShows.js";
import { useAccount } from "../../app/hooks/useAccount.js";
import { BANDSINTOWN_DASHBOARD_URL } from "../../app/vendorLinks.js";
import { dowShortDate, longDate, todayIso } from "./lib/bookingRows.js";
import { BANDSINTOWN_MAX_ROWS, bandsintownCsv, bandsintownLineup, bandsintownMissing } from "./lib/bandsintownCsv.js";
import styles from "./BandsintownExportModal.module.scss";

type ShowSummary = components["schemas"]["ShowSummary"];

export interface BandsintownExportModalProps {
  onClose: () => void;
}

/**
 * "Export for Bandsintown" (ART-128) — pick upcoming confirmed shows, download them as Bandsintown's bulk-upload
 * CSV, then tell the band where to drop it. It replaced the per-show copy-and-open hand-off: we never call
 * Bandsintown (its terms forbid commercial use of its data, and it has no public API for creating events), and a
 * file the band uploads themselves moves a whole run in one go.
 *
 * A show missing a field Bandsintown requires is listed but can't be ticked, with what it's missing, so the band
 * fixes it here rather than learning from a rejected upload. Selection stops at Bandsintown's 25-rows-per-file
 * limit.
 */
export function BandsintownExportModal({ onClose }: BandsintownExportModalProps) {
  const shows = useShows({ status: "confirmed", from: todayIso(), sort: "date", limit: 100 });
  const account = useAccount();
  const items = useMemo(() => shows.data?.items ?? [], [shows.data]);
  const ready = useMemo(() => items.filter((s) => bandsintownMissing(s).length === 0), [items]);

  // `null` = untouched: the default selection is every ready show up to the cap, computed from the data rather
  // than seeded into state, so it is right whenever the list lands.
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const selected = useMemo(
    () => picked ?? new Set(ready.slice(0, BANDSINTOWN_MAX_ROWS).map((s) => s.showId!)),
    [picked, ready],
  );
  // The shows in the downloaded file — kept so the last step can list what each event should carry.
  const [downloaded, setDownloaded] = useState<ShowSummary[]>([]);

  const atCap = selected.size >= BANDSINTOWN_MAX_ROWS;
  const allPicked = ready.length > 0 && ready.slice(0, BANDSINTOWN_MAX_ROWS).every((s) => selected.has(s.showId!));

  function toggle(id: string, next: boolean) {
    const copy = new Set(selected);
    if (next) copy.add(id);
    else copy.delete(id);
    setPicked(copy);
  }

  function toggleAll(next: boolean) {
    setPicked(next ? new Set(ready.slice(0, BANDSINTOWN_MAX_ROWS).map((s) => s.showId!)) : new Set());
  }

  function download() {
    const rows = items.filter((s) => selected.has(s.showId!));
    const csv = bandsintownCsv(rows, account.data?.artist);
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `bandsintown-shows-${todayIso()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setDownloaded(rows);
  }

  if (downloaded.length > 0) {
    return (
      <FormDialog
        title="Add your shows to Bandsintown"
        size="md"
        onClose={onClose}
        footer={
          <div className={styles.footer}>
            <Button variant="accent" onClick={onClose}>
              Done
            </Button>
          </div>
        }
      >
        <p className={styles.lead}>
          Your file with {downloaded.length} {downloaded.length === 1 ? "show" : "shows"} is downloaded. To add them:
        </p>
        <ol className={styles.steps}>
          <li>
            Go to the <strong>Events</strong> page in{" "}
            <a href={BANDSINTOWN_DASHBOARD_URL} target="_blank" rel="noopener noreferrer">
              Bandsintown for Artists
            </a>
            .
          </li>
          <li>
            Click <strong>Bulk Upload</strong>.
          </li>
          <li>Add the CSV file.</li>
          <li>
            <strong>Check each lineup.</strong> Bandsintown can drop artists it can&rsquo;t match. Add any missing ones
            in their editor.
          </li>
        </ol>
        {/* What the file sent, per show — the same list the CSV's Lineup column carries (`bandsintownLineup`), so a
            dropped artist is visible by comparison. We can't read the event back: Bandsintown's terms forbid it. */}
        <ul className={styles.lineups} aria-label="Lineups in the file">
          {downloaded.map((s) => (
            <li key={s.showId} className={styles.lineup}>
              <span className={styles.lineupShow}>
                {s.date ? dowShortDate(s.date) : ""} · {s.venueName || "TBA"}
              </span>
              <span className={styles.lineupActs}>
                {bandsintownLineup(s, account.data?.artist).join(", ") || "No artists"}
              </span>
            </li>
          ))}
        </ul>
      </FormDialog>
    );
  }

  return (
    <FormDialog
      title="Export for Bandsintown"
      size="md"
      onClose={onClose}
      // Ticking shows to export is a selection, not a draft — leaving needs no question.
      dirty={false}
      footer={
        <div className={styles.footer}>
          <DialogCancel />
          <Button variant="accent" disabled={selected.size === 0} onClick={download}>
            Download CSV
          </Button>
        </div>
      }
    >
      {shows.isLoading ? (
        <div className={styles.skeletons}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="block" height={40} />
          ))}
        </div>
      ) : shows.isError ? (
        <EmptyState
          title="Couldn't load your shows"
          action={<Button onClick={() => void shows.refetch()}>Retry</Button>}
        />
      ) : items.length === 0 ? (
        <EmptyState title="No upcoming confirmed shows" description="Confirm a show to export it." />
      ) : (
        <>
          <div className={styles.head}>
            <Checkbox
              checked={allPicked}
              onChange={toggleAll}
              disabled={ready.length === 0}
              label="Select all"
            />
            <span className={styles.count}>
              {selected.size} of {Math.min(ready.length, BANDSINTOWN_MAX_ROWS)} selected
            </span>
          </div>
          <ul className={styles.list}>
            {items.map((s) => (
              <ExportRow
                key={s.showId}
                show={s}
                checked={selected.has(s.showId!)}
                capped={atCap}
                onChange={(next) => toggle(s.showId!, next)}
              />
            ))}
          </ul>
          {ready.length > BANDSINTOWN_MAX_ROWS && (
            <p className={styles.note}>
              Bandsintown takes up to {BANDSINTOWN_MAX_ROWS} shows per file. Export the rest in another file.
            </p>
          )}
        </>
      )}
    </FormDialog>
  );
}

function ExportRow({
  show,
  checked,
  capped,
  onChange,
}: {
  show: ShowSummary;
  checked: boolean;
  capped: boolean;
  onChange: (next: boolean) => void;
}) {
  const missing = bandsintownMissing(show);
  const place = [show.city, show.state].filter(Boolean).join(", ");
  const label = (
    <span className={styles.rowText}>
      <span className={styles.rowName}>
        {(show.date && longDate(show.date)) || show.date} · {show.venueName || "TBA"}
      </span>
      {missing.length > 0 ? (
        <span className={styles.rowMissing}>Missing {missing.join(", ")}</span>
      ) : (
        place && <span className={styles.rowSub}>{place}</span>
      )}
    </span>
  );
  return (
    <li className={styles.row}>
      <Checkbox
        checked={checked}
        onChange={onChange}
        disabled={missing.length > 0 || (capped && !checked)}
        label={label}
      />
    </li>
  );
}
